import React, { useState } from 'react';
import { 
  makeStyles, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  Box,
  Fade,
  Zoom,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import { 
  Person, 
  Lock, 
  Visibility,
  VisibilityOff,
  ArrowForward
} from '@material-ui/icons';
import { useDispatch, useSelector } from "react-redux";
import { signIn } from '../../redux/actions/authActions';
import { useNavigate } from 'react-router';

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${require('../../../src/images/pha2.jpg')})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
  },
  animatedOrbs: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
    '&::before': {
      content: '""',
      position: "absolute",
      top: -160,
      right: -160,
      width: 320,
      height: 320,
      backgroundColor: "rgba(34, 109, 104, 0.2)",
      borderRadius: "50%",
      filter: "blur(48px)",
      animation: "$pulse 4s ease-in-out infinite",
    },
    '&::after': {
      content: '""',
      position: "absolute",
      bottom: -160,
      left: -160,
      width: 320,
      height: 320,
      backgroundColor: "rgba(242, 101, 34, 0.2)",
      borderRadius: "50%",
      filter: "blur(48px)",
      animation: "$pulse 4s ease-in-out infinite 1s",
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 0.5,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.8,
      transform: 'scale(1.1)',
    },
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: 400,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: 24,
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    overflow: "hidden",
    transition: "all 0.3s ease",
    '&:hover': {
      transform: "translateY(-4px)",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: "90%",
      margin: theme.spacing(1),
    },
  },
  headerSection: {
    position: "relative",
    background: "linear-gradient(135deg, #226D68 0%, #2a857e 100%)",
    padding: theme.spacing(6, 4, 4),
    overflow: "hidden",
  },
  decorativeCircle: {
    position: "absolute",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    '&:nth-child(1)': {
      top: -64,
      right: -64,
      width: 128,
      height: 128,
    },
    '&:nth-child(2)': {
      bottom: -48,
      left: -48,
      width: 96,
      height: 96,
    },
  },
  iconContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },
  iconWrapper: {
    width: 80,
    height: 80,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(10px)",
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },
  icon: {
    color: "white",
    fontSize: 40,
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "white",
    textAlign: "center",
    marginBottom: theme.spacing(1),
    letterSpacing: "-0.5px",
  },
  subtitle: {
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    fontSize: "0.9rem",
  },
  formContainer: {
    padding: theme.spacing(4),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  inputContainer: {
    position: "relative",
  },
  inputWrapper: {
    position: "relative",
  },
  inputIcon: {
    position: "absolute",
    left: 16,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#226D68",
    zIndex: 2,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 16,
      backgroundColor: "rgba(248, 250, 252, 0.9)",
      border: "2px solid rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease",
      '&:hover': {
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderColor: "#226D68",
      },
      '&.Mui-focused': {
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderColor: "#226D68",
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: "16px 16px 16px 48px",
      fontSize: "1rem",
      '&::placeholder': {
        color: "rgba(0, 0, 0, 0.5)",
        opacity: 1,
      },
    },
  },
  floatingLabel: {
    position: "absolute",
    left: 48,
    transition: "all 0.2s ease",
    pointerEvents: "none",
    zIndex: 2,
    backgroundColor: "white",
    padding: "0 8px",
    '&.visible': {
      top: -10,
      fontSize: "0.75rem",
      color: "#226D68",
      fontWeight: 600,
    },
    '&.hidden': {
      top: 16,
      color: "rgba(0, 0, 0, 0.5)",
      opacity: 0,
    },
  },
  passwordToggle: {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    color: "rgba(0, 0, 0, 0.4)",
    transition: "color 0.2s ease",
    zIndex: 2,
    '&:hover': {
      color: "#226D68",
      backgroundColor: "transparent",
    },
  },
  button: {
    borderRadius: 16,
    padding: theme.spacing(1.75),
    fontSize: "1.1rem",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #226D68 0%, #2a857e 100%)",
    boxShadow: "0 8px 25px rgba(34, 109, 104, 0.3)",
    transition: "all 0.3s ease",
    marginTop: theme.spacing(1),
    '&:hover': {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 30px rgba(34, 109, 104, 0.4)",
      background: "linear-gradient(135deg, #1e615d 0%, #26746e 100%)",
    },
    '&:disabled': {
      background: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)",
      transform: "none",
      boxShadow: "none",
    },
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  arrowIcon: {
    transition: "transform 0.3s ease",
  },
  footer: {
    marginTop: theme.spacing(3),
    textAlign: "center",
    color: "rgba(0, 0, 0, 0.6)",
    fontSize: "0.9rem",
  },
  decorativeBottom: {
    position: "absolute",
    bottom: -16,
    right: -16,
    width: 96,
    height: 96,
    backgroundColor: "rgba(34, 109, 104, 0.2)",
    borderRadius: "50%",
    filter: "blur(24px)",
    zIndex: -1,
  },
  decorativeTop: {
    position: "absolute",
    top: -16,
    left: -16,
    width: 128,
    height: 128,
    backgroundColor: "rgba(242, 101, 34, 0.2)",
    borderRadius: "50%",
    filter: "blur(24px)",
    zIndex: -1,
  },
}));

const Login = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [creds, setCreds] = useState({
    speudo: "",
    passwd: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({
    speudo: false,
    passwd: false,
  });

  const isFormValid = creds.speudo.trim() !== "" && creds.passwd.trim() !== "";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    
    dispatch(signIn(creds.speudo, creds.passwd));
    navigate("/dashboard");
    setCreds({ speudo: "", passwd: "" });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleFocus = (field) => {
    setIsFocused({ ...isFocused, [field]: true });
  };

  const handleBlur = (field) => {
    setIsFocused({ ...isFocused, [field]: false });
  };

  if (localStorage.getItem("token")) {
    navigate("/dashboard");
    return null;
  }

  return (
    <Box className={classes.container}>
      {/* Animated Orbs */}
      <div className={classes.animatedOrbs} />

      <Fade in={true} timeout={600}>
        <Card className={classes.card}>
          {/* Header Section */}
          <Box className={classes.headerSection}>
            {/* Decorative Circles */}
            <div className={classes.decorativeCircle} />
            <div className={classes.decorativeCircle} />

            {/* Icon */}
            <Box className={classes.iconContainer}>
              <Zoom in={true} timeout={600} style={{ transitionDelay: '200ms' }}>
                <Box className={classes.iconWrapper}>
                  <Person className={classes.icon} />
                </Box>
              </Zoom>
            </Box>

            {/* Title and Subtitle */}
            <Typography variant="h1" className={classes.title}>
              Connexion
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
              Accédez à votre espace personnel
            </Typography>
          </Box>

          {/* Form Section */}
          <Box className={classes.formContainer}>
            <form onSubmit={handleSubmit} className={classes.form}>
              
              {/* Username Field */}
              <Box className={classes.inputContainer}>
                <Box className={classes.inputWrapper}>
                  <Person className={classes.inputIcon} />
                  <TextField
                    className={classes.textField}
                    variant="outlined"
                    fullWidth
                    value={creds.speudo}
                    onChange={(e) => setCreds({ ...creds, speudo: e.target.value })}
                    onFocus={() => handleFocus('speudo')}
                    onBlur={() => handleBlur('speudo')}
                    placeholder={isFocused.speudo || creds.speudo ? '' : "Nom d'utilisateur"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start" style={{ width: 0 }} />,
                    }}
                  />
                  <label
                    className={`${classes.floatingLabel} ${
                      isFocused.speudo || creds.speudo ? 'visible' : 'hidden'
                    }`}
                  >
                    Nom d'utilisateur
                  </label>
                </Box>
              </Box>

              {/* Password Field */}
              <Box className={classes.inputContainer}>
                <Box className={classes.inputWrapper}>
                  <Lock className={classes.inputIcon} />
                  <TextField
                    className={classes.textField}
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    value={creds.passwd}
                    onChange={(e) => setCreds({ ...creds, passwd: e.target.value })}
                    onFocus={() => handleFocus('passwd')}
                    onBlur={() => handleBlur('passwd')}
                    placeholder={isFocused.passwd || creds.passwd ? '' : "Mot de passe"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start" style={{ width: 0 }} />,
                      endAdornment: (
                        <InputAdornment position="end" style={{ position: 'absolute', right: 16 }}>
                          <IconButton
                            className={classes.passwordToggle}
                            onClick={handleClickShowPassword}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <label
                    className={`${classes.floatingLabel} ${
                      isFocused.passwd || creds.passwd ? 'visible' : 'hidden'
                    }`}
                  >
                    Mot de passe
                  </label>
                </Box>
              </Box>

              {/* Submit Button */}
              <Button
                variant="contained"
                className={classes.button}
                disabled={!isFormValid}
                type="submit"
                fullWidth
                size="large"
              >
                <Box className={classes.buttonContent}>
                  Se Connecter
                  <ArrowForward className={classes.arrowIcon} style={{ 
                    transform: isFormValid ? 'translateX(2px)' : 'none' 
                  }} />
                </Box>
              </Button>
            </form>

            {/* Footer */}
            <Typography variant="body2" className={classes.footer}>
              Sécurisé par votre snstock
            </Typography>
          </Box>

          {/* Decorative Elements */}
          <div className={classes.decorativeBottom} />
          <div className={classes.decorativeTop} />
        </Card>
      </Fade>
    </Box>
  );
};

export default Login;